/**
 * Credits-pool transaction types: one classification, shared by every page.
 *
 * This file exists because there were two copies of these sets — one in the
 * admin console, one in the reseller's own dashboard — and they had drifted.
 * The same ledger row was rendered as a green credit on one page and a red
 * deduction on the other, and `REFUND_CREDITS_SETTLE` showed "-840.00" next to
 * a balance column that plainly went *up* by 840 on the same line.
 *
 * The membership below is not a judgement about what the names mean. It was
 * derived from production: for every type, whether `balanceAfter` was greater
 * or smaller than `balanceBefore` across all 700k+ rows. No type was mixed, so
 * each one lands in exactly one bucket, and a type absent from every set is
 * treated as a deduction — the safe default, since it is the reading that never
 * overstates what a reseller has.
 *
 * Amounts are stored inconsistently signed (DEDUCT positive, BULK_ASSIGN
 * negative), which is why callers show `Math.abs(credits)` and take the sign
 * from here rather than from the number.
 */

/** Balance went UP. */
export const POSITIVE_TYPES = new Set([
    'REFUND',
    'BULK_REFUND',
    'API_REFUND',
    'ADMIN_ADD',
    'ADMIN_GOLD_ADD',
    'GC_FROM_CREDITS',
    'REFUND_CREDITS_SETTLE',
    'MARKETPLACE_REFUND',
    'duration_downgrade',
    'SELF_TOPUP'
]);

/**
 * Balance unchanged: these record something that happened, not money moving.
 * Shown grey, because colouring them red implies a cost that was never charged.
 */
export const NEUTRAL_TYPES = new Set([
    'SELF_SWITCH_ORG',
    'SELF_EMAIL_CHANGE',
    'TRANSFER',
    'TRANSFER_ORG',
    'AUTO_EXPIRED',
    'NO_REFUND',
    'API_NO_REFUND',
    'BULK_DELETE',
    'REFUND_CREDITS_PENDING',
    'BULK_REFUND_CREDITS_PENDING',
    'API_REFUND_CREDITS_PENDING',
    'REFUND_DAYS_CLEAR',
    'API_KEY_GENERATED',
    'API_KEY_REVOKED'
]);

/**
 * Credits unchanged, but refund DAYS were granted.
 *
 * Kept apart from POSITIVE_TYPES because these rows are positive in days and
 * zero in credits; the row renders "+N day(s)" and must not claim a credit.
 */
export const DAYS_POSITIVE_TYPES = new Set([
    'REFUND_DAYS_ADD',
    'BULK_REFUND_DAYS_ADD',
    'API_REFUND_DAYS_ADD'
]);

/**
 * Gold Coins rather than Credits.
 *
 * Two currencies share this table, told apart only by the type string, so a
 * page that ignores this shows "+3000" for two very different things.
 */
export const GOLD_TYPES = new Set([
    'GC_FROM_CREDITS',
    'ADMIN_GOLD_ADD',
    'ADMIN_GOLD_DEDUCT',
    'MARKETPLACE_PURCHASE',
    'MARKETPLACE_REFUND'
    // SELF_TOPUP is NOT here: a bot top-up adds Credits, the balance the
    // Credits Pool actually spends, not the marketplace's Gold Coins.
]);

/** Raw types are for the database; these are for people. */
export const TYPE_LABELS = {
    CREDITS_TO_GC: 'Credits converted to GC',
    GC_FROM_CREDITS: 'GC received from Credits',
    SELF_TOPUP: 'Self-Service Top-up',
    ADMIN_GOLD_ADD: 'Gold added',
    ADMIN_GOLD_DEDUCT: 'Gold deducted',
    MARKETPLACE_PURCHASE: 'Shop purchase',
    MARKETPLACE_REFUND: 'Shop refund',
    REFUND_CREDITS_SETTLE: 'Pending refund settled',
    REFUND_CREDITS_PENDING: 'Refund pending',
    BULK_REFUND_CREDITS_PENDING: 'Bulk refund pending',
    API_REFUND_CREDITS_PENDING: 'API refund pending',
    REFUND_DAYS_ADD: 'Refund days added',
    BULK_REFUND_DAYS_ADD: 'Bulk refund days added',
    API_REFUND_DAYS_ADD: 'API refund days added',
    REFUND_DAYS_CLEAR: 'Refund days cleared',
    SELF_SWITCH_ORG: 'Self-service org switch',
    SELF_EMAIL_CHANGE: 'Self-service email change',
    REDEEM_KEYS_PURCHASE: 'Key redemption purchase',
    duration_upgrade: 'Duration upgrade',
    duration_downgrade: 'Duration downgrade'
};

export const isPositiveTransaction = (type) => POSITIVE_TYPES.has(type) || DAYS_POSITIVE_TYPES.has(type);
export const isNeutralTransaction = (type) => NEUTRAL_TYPES.has(type);
export const isGoldTransaction = (type) => GOLD_TYPES.has(type);
export const transactionTypeLabel = (type) => TYPE_LABELS[type] || type;

/** Green for money in, grey for a note, red for money out. */
export const transactionBadgeClass = (type) => {
    if (POSITIVE_TYPES.has(type) || DAYS_POSITIVE_TYPES.has(type)) return 'badge-success';
    if (NEUTRAL_TYPES.has(type)) return 'badge-secondary';
    return 'badge-danger';
};
